import Ajax from './Ajax';
import ContentType from './ContentType';
import Event from '../domain/Event';
import Token from '../domain/Token';
import User from '../domain/User';
import Participant from '../domain/Participant';
import Course from '../domain/Course';
import Center from '../domain/Center';
import _ from 'lodash';

const BaseAddress = '/';

const WebApi = {
    login(username, password) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/token',
                {grant_type: 'password', username, password},
                ContentType.JSON,
                null)
            .then(token => {
                return {
                    user: new User({id: token.user_id, username, permissions: token.permissions}),
                    token: new Token(token),
                };
            });
    },

    getSelectedEvent(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/events/selected',
                null,
                ContentType.JSON,
                token.access_token)
            .then(event => new Event(event));
    },

    updateToken(token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/token',
                {grant_type: 'refresh_token', refresh_token: token.refresh_token},
                ContentType.JSON,
                null)
            .then(token => new Token(token));
    },

    verifyCpf(cpf, event, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/subscriptions/participant/check',
                {cpf, eventId: event.id},
                ContentType.JSON,
                token.access_token)
            .then(participant => new Participant(participant));
    },

    printBadge(usualName, participant, event, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/subscriptions/badge/print',
                {usualName, participantId: participant.id, eventId: event.id},
                ContentType.JSON,
                token.access_token)
            .then(participant => new Participant(participant));
    },

    linkCourseware(participant, event, coursewareId, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/subscriptions/courseware/link',
                {participantId: participant.id, eventId: event.id, coursewareId},
                ContentType.JSON,
                token.access_token)
            .then(participant => new Participant(participant));
    },

    printTickets(course, coursewareIds, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/courses/' + course.id + '/coursewares/print',
                {ids: coursewareIds},
                ContentType.JSON,
                token.access_token)
            .then(result => result.filename);
    },

    selectCountries(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/countries/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data); // TODO: transformar em immutable
    },

    selectCities(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/cities/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data); // TODO: transformar em immutable
    },

    selectCourses(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/courses/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => _.map(result.data, function (course) { return new Course(course); }));
    },

    selectCenters(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/centers/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => _.map(result.data, function (center) { return new Center(center); }));
    },

    selectEvents(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/events/list',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => _.map(result.data, function (event) { return new Event(event); }));
    },

    selectPermissions(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/permissions/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data);
    },

    selectProfiles(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/profiles/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data);
    },

    selectUsers(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/users/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data);
    },

    selectAttendanceColumns(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/attendance/select/columns',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    selectEvent(id, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/events/select',
                {id},
                ContentType.JSON,
                token.access_token)
            .then(event => event ? new Event(event) : null);
    },

    listDashboard(token, eventId) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/reports/dashboard',
                $.param(_.pickBy({eventId}, _.identity)),
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listVendasTotais(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/reports/vendas/total',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listVendasDosNucleos(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/reports/vendas/dos_nucleos',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listVendasPorNucleos(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/reports/vendas/por_nucleos',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listVendasPorProdutos(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/reports/vendas/por_produtos',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data);
    },

    selectProductCategories(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/product/categories/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result.data);
    },

    importSheet(sheet, event, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/participants/upload',
                {sheet, eventId: event.id},
                ContentType.JSON,
                token.access_token);
    },

    importSheetProducts(sheet, event, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/produtos/upload',
                {sheet, eventId: event.id},
                ContentType.JSON,
                token.access_token);
    },

    listProducts(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/produtos/lista',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    selectProducts(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/products/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    createCart(token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/carrinho/novo',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    updateCartItem(cartId, update, token) {
      return Ajax
          .postAsync(
              BaseAddress + 'api/carrinho/atualizar/' + cartId,
              update,
              ContentType.JSON,
              token.access_token)
          .then(result => result);
    },

    listCartItems(cartId, token) {
      return Ajax
          .getAsync(
              BaseAddress + 'api/carrinho/info/' + cartId,
              null,
              ContentType.JSON,
              token.access_token)
          .then(result => result.items);
    },

    closeCart(cartId, id_cliente, token) {
      return Ajax
          .postAsync(
              BaseAddress + 'api/carrinho/finalizar/' + cartId,
              {id_cliente},
              ContentType.JSON,
              token.access_token)
          .then(result => result);
    },

    deleteCart(cartId, token) {
      return Ajax
          .deleteAsync(
              BaseAddress + 'api/carrinho/remover/' + cartId,
              null,
              ContentType.JSON,
              token.access_token)
          .then(result => result);
    },

    listOpenOrders(token) {
      return Ajax
          .getAsync(
              BaseAddress + 'api/carrinho/aberto',
              null,
              ContentType.JSON,
              token.access_token)
          .then(result => result);
    },

    listPaymentMethods(cartId, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/carrinho/pagamento',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    closeOrder(cartId, payment, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/carrinho/pagamento/' + cartId,
                payment,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    printOrderReceipt(orderId, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/carrinho/imprimir/recibo/' + orderId,
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    printOrderDelivery(orderId, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/carrinho/imprimir/expedicao/' + orderId,
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listClosedOrders(token) {
      return Ajax
          .getAsync(
              BaseAddress + 'api/carrinho/entregar',
              null,
              ContentType.JSON,
              token.access_token)
          .then(result => result);
    },

    deliverOrder(cartId, token) {
        return Ajax
            .postAsync(
                BaseAddress + 'api/carrinho/entregar/' + cartId,
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    findClientName(cpf, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/participants/name/' + cpf,
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listPaidOrders(token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/carrinho/pagos',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listParticipants(eventId, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/events/' + eventId + '/participants/select',
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listPaymentForOrder(cartId, token) {
        return Ajax
            .getAsync(
                BaseAddress + 'api/carrinho/pagamento/' + cartId,
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    listAmountBySlot(token, eventId, inicio, fim) {
        return Ajax
            .getAsync(
                `${BaseAddress}api/reports/distribuicao_valores`,
                $.param(_.pickBy({eventId, inicio, fim}, _.identity)),
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    createLotteryGame(eventId, token) {
        return Ajax
            .postAsync(
                `${BaseAddress}api/sorteio/${eventId}/novo`,
                null,
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },

    updateLotteryGame(gameId, winners, criteria, token) {
        return Ajax
            .postAsync(
                `${BaseAddress}api/sorteio/atualizar/${gameId}`,
                {winners, criteria},
                ContentType.JSON,
                token.access_token)
            .then(result => result);
    },
};

export default WebApi;



// WEBPACK FOOTER //
// src/web/WebApi.js